import { Carousel } from '@mantine/carousel'
import { Center, Stack, Title, useMantineTheme } from '@mantine/core'
import { useMediaQuery } from '@mantine/hooks'
import { IconCaretLeftFilled, IconCaretRightFilled } from '@tabler/icons-react'
import React, { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'

import { useErrorMessages } from '@/constants/content'
import useSessionStorage from '@/hooks/useSessionStorage'
import { Entry, GUIDES_UPDATES_CONTENT_TYPE } from '@/plugins/contentful/api'
import client from '@/plugins/contentful/client'

import {
  GalleryElementsImagesByEntryId,
  pullGalleryImage,
  wrapData,
} from '../Discover/DiscoverCarousel'
import ErrorMessage from '../ErrorMessage'
import { CTACard } from '../GetStarted'
import Loading from '../Loading'

const GuidesUpdatesCarousel: React.FC = () => {
  const {
    t,
    i18n: { language },
  } = useTranslation()
  const theme = useMantineTheme()
  const isMobile = useMediaQuery(`(max-width: ${theme.breakpoints.sm})`)

  const errorMessages = useErrorMessages()
  const [isLoading, setIsLoading] = useState<boolean>(false)
  const [error, setError] = useState<string>('')
  const [hasFetched, setHasFetched] = useState<boolean>(false)
  const [guidesUpdates, setGuidesUpdates] = useSessionStorage<Entry[]>(
    `guidesUpdates-${language}`,
    []
  )
  const [galleryImageByEntry, setGalleryImageByEntry] = useState<GalleryElementsImagesByEntryId>({})

  useEffect(() => {
    if (!guidesUpdates.length && !hasFetched) {
      setIsLoading(true)
      client
        .getEntries({ content_type: GUIDES_UPDATES_CONTENT_TYPE, locale: language })
        .then((response: any) => {
          const sortedContent = response.items.sort(
            (a: Entry, b: Entry) => Number(a.fields.order) - Number(b.fields.order)
          )
          setGuidesUpdates(sortedContent)
          if (response.items.length < 1) {
            setHasFetched(true)
          }
        })
        .catch((err: any) => {
          setError(err.message)
          setIsLoading(false)
        })
        .finally(() => {
          setIsLoading(false)
        })
    }
  }, [guidesUpdates, hasFetched, setGuidesUpdates, t, language])

  useEffect(() => {
    if (guidesUpdates) {
      setGalleryImageByEntry(oldGalleryImageByEntry =>
        pullGalleryImage(guidesUpdates, oldGalleryImageByEntry, setGalleryImageByEntry)
      )
    }
  }, [guidesUpdates])

  return (
    <Center style={{ margin: 0 }}>
      {isLoading && <Loading />}
      {error && <ErrorMessage message={errorMessages.cmsConnection} details={error} />}
      {!isLoading && !error && (
        <Stack style={{ gap: '30px', alignItems: 'left', width: '100%' }}>
          <Title order={isMobile ? 3 : 2}>
            {t(`components.guidesUpdates.titleHomepage`, 'Guides & Updates')}
          </Title>

          <Stack
            style={{
              alignItems: 'center',
              width: '100%',
            }}
          >
            <Carousel
              slideSize={`calc(${100 / guidesUpdates.length}% - 30px)`}
              previousControlIcon={<IconCaretLeftFilled />}
              nextControlIcon={<IconCaretRightFilled />}
              slidesToScroll="auto"
            >
              {guidesUpdates &&
                guidesUpdates.map((elem, index) => {
                  const card = wrapData(elem, galleryImageByEntry)
                  return (
                    <Carousel.Slide key={`${elem}${String(index)}`}>
                      <CTACard
                        image={card.image}
                        title={card.title}
                        button={{
                          text: card.buttonText,
                          link: card.buttonLink,
                        }}
                      />
                    </Carousel.Slide>
                  )
                })}
            </Carousel>
          </Stack>
        </Stack>
      )}
    </Center>
  )
}

export default GuidesUpdatesCarousel
